import React from 'react'
import type { AdvancedFeatureResponse } from '#/api/models/advancedFeatureResponse'
import type { BulkActionResponse } from '#/api/models/bulkActionResponse'
import type { DataResponse } from '#/api/models/dataResponse'
import type { DataSupplementResponse } from '#/api/models/dataSupplementResponse'
import type { SupplementalDataVersionItemAutomatic } from '#/api/models/supplementalDataVersionItemAutomatic'
import type { AssetResponse } from '#/dataInterface'
import {
  getLatestTranscriptVersionItem,
  isSupplementVersionAutomatic,
  isSupplementVersionWithValue,
} from '../../common/utils'
import TranscriptCreate from './TranscriptCreate'
import TranscriptEdit from './TranscriptEdit'
import TranscriptPoll from './TranscriptPoll'

type VersionOfAutomaticTranscript = SupplementalDataVersionItemAutomatic

interface Props {
  asset: AssetResponse
  questionXpath: string
  submission: DataResponse
  activeBulkActions: BulkActionResponse[]
  onUnsavedWorkChange: (hasUnsavedWork: boolean) => void
  supplement: DataSupplementResponse
  advancedFeatures: AdvancedFeatureResponse[]
}

export default function TranscriptTab({
  asset,
  questionXpath,
  submission,
  activeBulkActions,
  onUnsavedWorkChange,
  supplement,
  advancedFeatures,
}: Props) {
  const transcriptVersion = getLatestTranscriptVersionItem(supplement, questionXpath)

  // We need a key to force remounting the component when switching between submissions.
  const submissionKey = submission._uuid

  if (
    transcriptVersion &&
    isSupplementVersionAutomatic(transcriptVersion) &&
    (transcriptVersion as VersionOfAutomaticTranscript)?._data?.status === 'in_progress'
  ) {
    return <TranscriptPoll key={submissionKey} asset={asset} questionXpath={questionXpath} submission={submission} />
  }
  if (transcriptVersion && isSupplementVersionWithValue(transcriptVersion)) {
    return (
      <TranscriptEdit
        key={submissionKey}
        asset={asset}
        questionXpath={questionXpath}
        submission={submission}
        supplement={supplement}
        transcriptVersion={transcriptVersion}
        activeBulkActions={activeBulkActions}
        onUnsavedWorkChange={onUnsavedWorkChange}
        advancedFeatures={advancedFeatures}
      />
    )
  }

  return (
    <TranscriptCreate
      key={submissionKey}
      asset={asset}
      questionXpath={questionXpath}
      submission={submission}
      supplement={supplement}
      activeBulkActions={activeBulkActions}
      onUnsavedWorkChange={onUnsavedWorkChange}
      advancedFeatures={advancedFeatures}
    />
  )
}
